import {
  applyContactNormalizationBatch,
  applyContactNormalizationRecord,
  fetchContactNormalizationQueue,
  fetchContactNormalizationQueueRecord,
  scanContactNormalizationQueue,
  updateContactNormalizationQueueRecord,
} from "@/Utile/ncrmContactNormalizationApi.mjs";

export const contactNormalizationQueueActions = {
  resetAndFetch() {
    this.page = 1;
    this.selectedIds = [];
    this.records = [];
    if (this.workflowInstanceId) this.fetchRecords();
  },
  async fetchRecords() {
    if (!this.workflowInstanceId) return;
    this.loading = true;
    this.localError = "";
    try {
      const { data } = await fetchContactNormalizationQueue({
        workflow_instance_id: this.workflowInstanceId,
        node_type: this.nodeType,
        status: this.statusFilter || undefined,
        page: this.page,
        per_page: this.perPage,
      });
      this.records = Array.isArray(data?.records) ? data.records : [];
      this.total = Number(data?.total || 0);
    } catch (error) {
      this.localError = error?.response?.data?.error || this.$t("ncrm.workflows.contactNormalization.errors.load");
    } finally {
      this.loading = false;
    }
  },
  async scanNode() {
    this.scanning = true;
    this.localError = "";
    this.localMessage = "";
    try {
      const { data } = await scanContactNormalizationQueue({
        workflow_instance_id: this.workflowInstanceId,
        node_type: this.nodeType,
      });
      this.localMessage = this.$t("ncrm.workflows.contactNormalization.scanDone", {
        scanned: data?.scanned || 0,
        queued: data?.queued || 0,
        ready: data?.ready || 0,
        review: data?.needs_review || 0,
      });
      this.page = 1;
      await this.fetchRecords();
    } catch (error) {
      this.localError = error?.response?.data?.error || this.$t("ncrm.workflows.contactNormalization.errors.scan");
    } finally {
      this.scanning = false;
    }
  },
  async applySelected() {
    if (!this.selectedIds.length) return;
    this.applyingBatch = true;
    this.localError = "";
    this.localMessage = "";
    try {
      const { data } = await applyContactNormalizationBatch(this.workflowInstanceId, this.selectedIds, {
        apply_review_proposals: true,
      });
      this.localMessage = this.$t("ncrm.workflows.contactNormalization.applyDone", {
        applied: data?.applied || 0,
        skipped: data?.skipped || 0,
        failed: data?.failed || 0,
      });
      this.selectedIds = [];
      await this.fetchRecords();
    } catch (error) {
      this.localError = error?.response?.data?.error || this.$t("ncrm.workflows.contactNormalization.errors.apply");
    } finally {
      this.applyingBatch = false;
    }
  },
  async openRecord(record) {
    this.drawer = true;
    this.selectedRecord = record;
    this.loadingRecord = true;
    try {
      const { data } = await fetchContactNormalizationQueueRecord(record.id, this.workflowInstanceId);
      this.selectedRecord = data || record;
    } catch (_) {
      this.selectedRecord = record;
    } finally {
      this.loadingRecord = false;
    }
  },
  handleRowClick(_, row) {
    if (row?.item) this.openRecord(row.item);
  },
  async saveCorrection({ record, proposed_values, note }) {
    await this.updateRecord(record, { proposed_values, note });
  },
  async skipRecord({ record, note }) {
    await this.updateRecord(record, { status: "skipped", note });
  },
  async updateRecord(record, patch) {
    if (!record?.id) return;
    this.savingRecord = true;
    this.localError = "";
    try {
      const { data } = await updateContactNormalizationQueueRecord(record.id, {
        workflow_instance_id: this.workflowInstanceId,
        ...patch,
      });
      if (data?.record) this.selectedRecord = data.record;
      await this.fetchRecords();
    } catch (error) {
      this.localError = error?.response?.data?.error || this.$t("ncrm.workflows.contactNormalization.errors.save");
    } finally {
      this.savingRecord = false;
    }
  },
  async applyRecord(record) {
    if (!record?.id) return;
    this.applyingRowId = record.id;
    this.localError = "";
    this.localMessage = "";
    try {
      const { data } = await applyContactNormalizationRecord(record.id, this.workflowInstanceId);
      this.localMessage = data?.result?.applied
        ? this.$t("ncrm.workflows.contactNormalization.applyRowDone")
        : this.$t("ncrm.workflows.contactNormalization.applyRowSkipped");
      await this.fetchRecords();
      if (data?.result?.applied && this.selectedRecord?.id === record.id) {
        this.drawer = false;
      }
      if (this.selectedRecord?.id === record.id) {
        const { data: detail } = await fetchContactNormalizationQueueRecord(record.id, this.workflowInstanceId);
        this.selectedRecord = detail || this.selectedRecord;
      }
    } catch (error) {
      this.localError = error?.response?.data?.error || this.$t("ncrm.workflows.contactNormalization.errors.apply");
    } finally {
      this.applyingRowId = null;
    }
  },
  formatValues(values = {}) {
    const visible = this.fields
      .map((field) => values?.[field])
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
      .map((value) => String(value).trim());
    return visible.length ? visible.join(" · ") : "—";
  },
  issueCount(item) {
    return Array.isArray(item?.issues) ? item.issues.length : 0;
  },
  hasReviewIssue(item) {
    return (Array.isArray(item?.issues) ? item.issues : []).some((issue) => issue.severity === "review");
  },
  statusColor(status) {
    if (status === "ready") return "success";
    if (status === "needs_review") return "warning";
    if (status === "applied") return "primary";
    if (status === "failed") return "error";
    return "neutral";
  },
};
